import { GameBus } from '../core/GameBus'
import pt from '../data/locales/pt.json'
import en from '../data/locales/en.json'
import es from '../data/locales/es.json'

export type Locale = 'pt' | 'en' | 'es'

const MESSAGES: Record<Locale, any> = { pt, en, es }

export class LocalizationSystem {
  private currentLocale: Locale = 'pt'

  constructor() {
    this.loadLocaleFromStorage()
    // Sincroniza o locale entre instâncias do React/Phaser via GameBus
    GameBus.on('locale:changed', (payload: { locale: Locale }) => {
      if (this.currentLocale !== payload.locale) {
        this.currentLocale = payload.locale
        this.persistLocale()
      }
    })
  }

  getLocale(): Locale {
    return this.currentLocale
  }

  setLocale(locale: Locale): void {
    if (this.currentLocale === locale) return
    this.currentLocale = locale
    this.persistLocale()
    GameBus.emit('locale:changed', { locale })
  }

  t(path: string): string {
    const parts = path.split('.')
    let current = MESSAGES[this.currentLocale]
    for (const part of parts) {
      if (!current || current[part] === undefined) return path
      current = current[part]
    }
    return typeof current === 'string' ? current : path
  }

  private loadLocaleFromStorage(): void {
    try {
      const saved = localStorage.getItem('cs_locale') as Locale
      if (saved && ['pt', 'en', 'es'].includes(saved)) {
        this.currentLocale = saved
        return
      }
      // Sem preferência salva: detecta pelo idioma do navegador
      this.currentLocale = this.detectBrowserLocale()
    } catch {
      this.currentLocale = 'pt'
    }
  }

  private detectBrowserLocale(): Locale {
    try {
      const candidates =
        (typeof navigator !== 'undefined' && navigator.languages?.length
          ? navigator.languages
          : [typeof navigator !== 'undefined' ? navigator.language : '']) || []
      for (const lang of candidates) {
        const code = (lang || '').toLowerCase().split('-')[0]
        if (code === 'pt' || code === 'en' || code === 'es') {
          return code as Locale
        }
      }
    } catch {
      /* ignore */
    }
    return 'pt'
  }

  private persistLocale(): void {
    try {
      localStorage.setItem('cs_locale', this.currentLocale)
    } catch {
      /* ignore */
    }
  }
}

export const L10n = new LocalizationSystem()
